/**
 * Recherche d'itinéraire sur la grille des cases de la carte (déplacement au
 * tap / clic) — pur calcul, sans Phaser. La grille est construite par
 * ZoneScene.buildWalkGrid() à partir de toutes les collisions réelles.
 *
 * - A* 8 directions, sans couper les coins (une diagonale exige que les deux
 *   cases adjacentes soient libres — sinon l'avatar accroche l'angle).
 * - Lissage : on saute les étapes intermédiaires tant que la ligne droite
 *   reste dans des cases libres (avec la largeur du corps de l'avatar), pour
 *   éviter une marche en escalier.
 */
export interface WalkGrid {
  width: number;
  height: number;
  tileSize: number;
  /** 1 = case bloquante. Index = y * width + x. */
  blocked: Uint8Array;
}

export interface Point { x: number; y: number }

/** Limite de cases explorées (garde-fou sur les très grandes cartes). */
const MAX_EXPANDED = 40_000;

export function isBlocked(grid: WalkGrid, cx: number, cy: number): boolean {
  if (cx < 0 || cy < 0 || cx >= grid.width || cy >= grid.height) return true;
  return grid.blocked[cy * grid.width + cx] === 1;
}

export function cellOf(grid: WalkGrid, p: Point): Point {
  return { x: Math.floor(p.x / grid.tileSize), y: Math.floor(p.y / grid.tileSize) };
}

function cellCenter(grid: WalkGrid, c: Point): Point {
  return { x: (c.x + 0.5) * grid.tileSize, y: (c.y + 0.5) * grid.tileSize };
}

/** Case libre la plus proche (parcours en largeur), ou null si aucune. */
export function nearestFreeCell(grid: WalkGrid, from: Point, maxRadius = 60): Point | null {
  // `from` en pixels : la case libre dont le centre est le plus proche de ce
  // point, pas la première trouvée dans un ordre de voisins fixe (qui
  // préférait la case du dessous — de l'autre côté d'un mur d'une case).
  const ts = grid.tileSize;
  const start = cellOf(grid, from);
  let best: Point | null = null;
  let bestDist = Infinity;
  for (let r = 0; r <= maxRadius; r++) {
    // Une case de l'anneau r est à au moins (r - 1) cases du point.
    if (best && (r - 1) * ts > bestDist) break;
    for (let cy = start.y - r; cy <= start.y + r; cy++) {
      for (let cx = start.x - r; cx <= start.x + r; cx++) {
        if (Math.max(Math.abs(cx - start.x), Math.abs(cy - start.y)) !== r) continue;
        if (isBlocked(grid, cx, cy)) continue;
        const d = Math.hypot((cx + 0.5) * ts - from.x, (cy + 0.5) * ts - from.y);
        if (d < bestDist) { bestDist = d; best = { x: cx, y: cy }; }
      }
    }
  }
  return best;
}

/** Tas binaire minimal sur f-score. */
class MinHeap {
  private items: number[] = [];
  constructor(private readonly score: Float64Array) {}
  get size(): number { return this.items.length; }
  push(i: number): void {
    const a = this.items;
    a.push(i);
    let k = a.length - 1;
    while (k > 0) {
      const p = (k - 1) >> 1;
      if (this.score[a[p]!]! <= this.score[a[k]!]!) break;
      [a[p], a[k]] = [a[k]!, a[p]!];
      k = p;
    }
  }
  pop(): number {
    const a = this.items;
    const top = a[0]!;
    const last = a.pop()!;
    if (a.length > 0) {
      a[0] = last;
      let k = 0;
      for (;;) {
        const l = 2 * k + 1;
        const r = l + 1;
        let m = k;
        if (l < a.length && this.score[a[l]!]! < this.score[a[m]!]!) m = l;
        if (r < a.length && this.score[a[r]!]! < this.score[a[m]!]!) m = r;
        if (m === k) break;
        [a[m], a[k]] = [a[k]!, a[m]!];
        k = m;
      }
    }
    return top;
  }
}

const NEIGHBORS = [
  [1, 0, 1], [-1, 0, 1], [0, 1, 1], [0, -1, 1],
  [1, 1, Math.SQRT2], [1, -1, Math.SQRT2], [-1, 1, Math.SQRT2], [-1, -1, Math.SQRT2],
] as const;

/**
 * Chemin en cases de `start` à `goal` (inclus), ou null si inatteignable.
 * Si `goal` est inatteignable, renvoie le chemin vers la case atteinte la
 * plus proche de `goal` (l'avatar s'approche au mieux au lieu de rester figé).
 */
export function findCellPath(grid: WalkGrid, start: Point, goal: Point): Point[] | null {
  const W = grid.width;
  const n = W * grid.height;
  const g = new Float64Array(n).fill(Infinity);
  const f = new Float64Array(n).fill(Infinity);
  const from = new Int32Array(n).fill(-1);
  const closed = new Uint8Array(n);
  const h = (x: number, y: number) => {
    const dx = Math.abs(x - goal.x);
    const dy = Math.abs(y - goal.y);
    return dx + dy + (Math.SQRT2 - 2) * Math.min(dx, dy);
  };

  const s = start.y * W + start.x;
  const goalIdx = goal.y * W + goal.x;
  g[s] = 0;
  f[s] = h(start.x, start.y);
  const open = new MinHeap(f);
  open.push(s);
  let best = s;
  let bestH = f[s]!;
  let expanded = 0;

  while (open.size > 0 && expanded < MAX_EXPANDED) {
    const cur = open.pop();
    if (closed[cur]) continue;
    closed[cur] = 1;
    expanded++;
    const cx = cur % W;
    const cy = (cur - cx) / W;
    const hh = h(cx, cy);
    if (hh < bestH) { bestH = hh; best = cur; }
    if (cur === goalIdx) { best = cur; break; }

    for (const [dx, dy, cost] of NEIGHBORS) {
      const nx = cx + dx;
      const ny = cy + dy;
      if (isBlocked(grid, nx, ny)) continue;
      if (dx !== 0 && dy !== 0 && (isBlocked(grid, cx + dx, cy) || isBlocked(grid, cx, cy + dy))) continue;
      const ni = ny * W + nx;
      if (closed[ni]) continue;
      const ng = g[cur]! + cost;
      if (ng >= g[ni]!) continue;
      g[ni] = ng;
      from[ni] = cur;
      f[ni] = ng + h(nx, ny);
      open.push(ni);
    }
  }

  if (best === s && s !== goalIdx) return null;
  const path: Point[] = [];
  for (let i = best; i !== -1; i = from[i]!) path.push({ x: i % W, y: Math.floor(i / W) });
  return path.reverse();
}

/**
 * La ligne droite de `a` à `b` (en pixels) reste-t-elle dans des cases libres,
 * en tenant compte de la demi-largeur du corps de l'avatar ?
 */
export function hasLineOfSight(grid: WalkGrid, a: Point, b: Point, halfWidth: number): boolean {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const len = Math.hypot(dx, dy);
  if (len === 0) return true;
  // Trois lignes parallèles : axe + deux bords du corps.
  const nx = (-dy / len) * halfWidth;
  const ny = (dx / len) * halfWidth;
  const step = grid.tileSize / 4;
  const steps = Math.ceil(len / step);
  for (const [ox, oy] of [[0, 0], [nx, ny], [-nx, -ny]] as const) {
    for (let k = 0; k <= steps; k++) {
      const t = k / steps;
      const px = a.x + dx * t + ox;
      const py = a.y + dy * t + oy;
      if (isBlocked(grid, Math.floor(px / grid.tileSize), Math.floor(py / grid.tileSize))) return false;
    }
  }
  return true;
}

/**
 * Itinéraire en pixels de `from` vers `to` : liste d'étapes, la dernière étant
 * `to` lui-même s'il est dans une case libre (sinon le centre de la case
 * libre la plus proche). Null si aucun déplacement possible.
 */
export function findPath(grid: WalkGrid, from: Point, to: Point, halfWidth: number): Point[] | null {
  // nearestFreeCell prend un point en pixels (pas une case) ; une case libre reste la sienne.
  const freeAt = (p: Point, maxRadius?: number): Point | null => {
    const c = cellOf(grid, p);
    return isBlocked(grid, c.x, c.y) ? nearestFreeCell(grid, p, maxRadius) : c;
  };
  const startCell = freeAt(from, 2);
  const rawGoal = cellOf(grid, to);
  const goalCell = freeAt(to);
  if (!startCell || !goalCell) return null;

  const cells = findCellPath(grid, startCell, goalCell);
  if (!cells || cells.length === 0) return null;

  const reachedGoal = cells[cells.length - 1]!;
  const exactGoal = reachedGoal.x === rawGoal.x && reachedGoal.y === rawGoal.y;
  const points = cells.map((c) => cellCenter(grid, c));
  points[0] = from;
  if (exactGoal) points[points.length - 1] = to;

  // Lissage par ligne de vue.
  const smoothed: Point[] = [];
  let anchor = points[0]!;
  let i = 1;
  while (i < points.length) {
    let j = points.length - 1;
    while (j > i && !hasLineOfSight(grid, anchor, points[j]!, halfWidth)) j--;
    smoothed.push(points[j]!);
    anchor = points[j]!;
    i = j + 1;
  }
  return smoothed;
}
